import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'
import stitchAxios from '../utils/axios'
import { BackendUser, BackendUserSortState, BackendUserSortableId } from '../types'
import axios from 'axios'

type BackendUsersObject = Record<string, BackendUser>

type Store = {
  backendUsers: BackendUsersObject,
  getBackendUsers: () => void,
  getBackendUser: (id: string) => void,
  updateBackendUser: (backendUser: BackendUser) => Promise<[boolean, string]>,
  toggleNotifications: (id: string, notificationsOn: boolean) => Promise<void>,
  rehydrated: boolean,
  setRehydrated: (isRehydrated: boolean) => void,
  searchTerm: string,
  sortState: BackendUserSortState,
  indexPageNumber: number,
  indexRowsPerPage: number,
}

const createFunction = (set: Function, get: Function): Store => ({
  backendUsers: {},
  getBackendUsers: async () => {
    const response = await stitchAxios.get('/backend_users')
    const backendUsersArray = response.data

    const backendUsers = {} as BackendUsersObject

    backendUsersArray.forEach((backendUser: BackendUser) => {
      backendUsers[backendUser.id] = backendUser
    })
    set((state: Store) => ({ ...state, backendUsers }))
  },
  getBackendUser: async (backendUserId: string): Promise<void> => {
    const resp = await stitchAxios.get(`/backend_users/${backendUserId}`)
    set((state: Store) => ({ ...state, backendUsers: { ...state.backendUsers, [backendUserId]: resp.data }, }))
  },
  updateBackendUser: async (backendUser: BackendUser): Promise<[boolean, string]> => {
    try {
      const resp = await stitchAxios.patch(`/backend_users/${backendUser.id}`, {
        ...backendUser
      })

      set((state: Store) => ({ ...state, backendUsers: { ...state.backendUsers, [backendUser.id]: resp.data }, }))
      return [true, '']
    } catch (e) {
      if (axios.isAxiosError(e)) {
        return [false, ((e.response && e.response.data.message) || 'Could not update backend user.')]
      }
      throw e
    }
  },
  toggleNotifications: async (id: string, notificationsOn: boolean): Promise<void> => {
    const backendUser = get().backendUsers[id]
    if (!backendUser) {
      throw new Error(`Backend User ID not found ${id}`)
    }

    await get().updateBackendUser({ ...backendUser, notificationsOn })
  },
  rehydrated: false,
  setRehydrated: (isRehydrated: boolean) => set({ rehydrated: isRehydrated }),
  sortState: {
    active: true,
    ascending: true,
    key: BackendUserSortableId.email,
  },
  indexPageNumber: 0,
  indexRowsPerPage: 10,
  searchTerm: ''
})

export const useBackendUserStore = create(devtools(persist(createFunction, {
  name: 'stitchBackendUsers',
  partialize: (state: Store) => ({ backendUsers: state.backendUsers }),
  onRehydrateStorage: (_: Store) => {
    return ((state?: Store | undefined, error?: unknown): void => {
      if (!state) {
        throw error
      }

      state.setRehydrated(true)
    })
  },
})))

export default useBackendUserStore
